'use client';

import { useState, useMemo, useEffect } from 'react';
import { useAppStore } from '@/stores/app-store';
import { usePackageInfo } from '@/components/PackageUsage';
import { t } from '@/lib/i18n';
import { userlevelToRole } from '@/lib/permissions';
import { UserRole } from '@/lib/types';
import Avatar from '@/components/Avatar';
import StatCard from '@/components/StatCard';
import UserModal from '../modals/UserModal';

/** Shape returned by GET /api/users (real database list). */
interface DbUserRow {
  id: number;
  name: string;
  email: string;
  userlevel: string | null;
  partner_group: string | null;
  status: string | null;
  last_signin: string | null;
  register_date: string | null;
}

/** Shape returned by GET /api/users with pagination. */
interface UsersResponse {
  users: DbUserRow[];
  total: number;
  page: number;
  pageSize: number;
  counts: { admin: number; owner: number; viewer: number };
}

const PAGE_SIZE = 7;

/** Compact date formatter for last-sign-in / registration columns. */
function fmtDate(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function UserManagementPage() {
  // โควตาผู้ใช้จากแพ็กเกจ — บัญชี Free ได้ user_level = 1 คือเห็นแค่ตัวเอง
  // ต้องบอกให้รู้ตรงนี้ ไม่งั้นหน้าจะดูเหมือนพัง แทนที่จะเป็นจุดชวนอัปเกรด
  const pkg = usePackageInfo();
  const currentUser = useAppStore((s) => s.currentUser);
  const dbUser = useAppStore((s) => s.dbUser);
  const users = useAppStore((s) => s.users);
  const plants = useAppStore((s) => s.plants);
  const lang = useAppStore((s) => s.lang);
  const deleteUser = useAppStore((s) => s.deleteUser);
  const toast = useAppStore((s) => s.toast);

  const [showUserModal, setShowUserModal] = useState(false);
  const [userModalMode, setUserModalMode] = useState<'add' | 'edit'>('add');
  const [editUserIdx, setEditUserIdx] = useState(0);
  const [editDbUser, setEditDbUser] = useState<{ id: number; name: string; email: string; role: UserRole } | null>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  // In production (DB) mode the table shows the real `usertable` list fetched
  // from /api/users. Demo mode keeps using the in-memory store users.
  const isDbMode = !!dbUser;
  const [dbUsers, setDbUsers] = useState<DbUserRow[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ admin: 0, owner: 0, viewer: 0 });

  // Search box. `query` is what's typed; `search` is the debounced value that
  // actually hits the API, so a fast typist doesn't fire a request per keystroke.
  // The debounce also resets to page 1 — page 3 of the previous result set means
  // nothing once the filter changes.
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => { setSearch(query.trim()); setPage(1); }, 300);
    return () => clearTimeout(id);
  }, [query]);

  // "Loading" is derived, not stored: whatever the effect last finished loading
  // versus what the current page/search asks for. Avoids a setState on every
  // request start just to drive a spinner.
  const reqKey = `${page}|${search}|${refreshKey}`;
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const loading = isDbMode && loadedKey !== reqKey;

  useEffect(() => {
    if (!isDbMode) return;
    let cancelled = false;
    fetch(`/api/users?page=${page}&pageSize=${PAGE_SIZE}&q=${encodeURIComponent(search)}`)
      .then(async (r) => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.error || `HTTP ${r.status}`);
        }
        return r.json() as Promise<UsersResponse>;
      })
      .then((data) => {
        if (cancelled) return;
        setDbUsers(data.users);
        setTotal(data.total);
        setCounts(data.counts);
        setLoadError(null);
      })
      .catch((e) => { if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Failed to load users'); })
      .finally(() => { if (!cancelled) setLoadedKey(reqKey); });
    return () => { cancelled = true; };
  }, [isDbMode, page, refreshKey, search, reqKey]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Demo mode has no server to search — filter the in-memory list here on the
  // same fields the API matches on (name / email / role). Each row carries its
  // index in the ORIGINAL array: edit/delete address the store by position, so
  // handing them a filtered index would act on the wrong user.
  const shownUsers = useMemo(() => {
    const rows = users.map((u, idx) => ({ u, idx }));
    if (isDbMode || !search) return rows;
    const q = search.toLowerCase();
    return rows.filter(({ u }) =>
      u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.role.toLowerCase().includes(q)
    );
  }, [isDbMode, users, search]);

  const stats = useMemo(() => {
    if (isDbMode) {
      // Whole-tenant figures from the server — deliberately NOT narrowed by the
      // search, so the cards stay a stable picture while filtering the table.
      return {
        total: counts.admin + counts.owner + counts.viewer,
        admins: counts.admin,
        owners: counts.owner,
        viewers: counts.viewer,
      };
    }
    const roles = users.map((u) => u.role);
    return {
      total: roles.length,
      admins: roles.filter((r) => r === 'admin').length,
      owners: roles.filter((r) => r === 'owner').length,
      viewers: roles.filter((r) => r === 'viewer').length,
    };
  }, [isDbMode, counts, users]);

  // ค่าเดียวกับ sidebar และ app/page.tsx — server ตัดสินมาแล้ว
  // (เดิมหน้านี้เช็ค role === 'admin' เอง ทำให้เมนูขึ้นแต่เปิดเข้ามาเจอ Access Denied)
  if (!currentUser || !(dbUser ? dbUser.canManageUsers === true : currentUser.role === 'admin')) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted, #94a3b8)', fontSize: 14 }}>
        {t('accessDenied', lang)}
      </div>
    );
  }

  const roleColors: Record<string, { bg: string; text: string }> = {
    admin: { bg: 'var(--red-dim)', text: 'var(--red)' },
    owner: { bg: 'var(--amber-dim)', text: 'var(--amber)' },
    viewer: { bg: 'var(--green-dim)', text: 'var(--green)' },
  };

  const panelStyle: React.CSSProperties = {
    background: 'var(--bg-surface)',
    borderRadius: 16,
    border: '1px solid var(--glass-border)',
    overflow: 'hidden',
    marginBottom: 24,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
  };

  const headStyle: React.CSSProperties = {
    padding: '14px 20px',
    fontSize: 15,
    fontWeight: 800,
    letterSpacing: '-0.02em',
    color: 'var(--text-primary)',
    borderBottom: '1px solid var(--border-subtle)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const btnStyle: React.CSSProperties = {
    padding: '6px 16px',
    fontSize: 12,
    fontWeight: 700,
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    color: '#0C1222',
    background: 'linear-gradient(135deg, var(--amber), var(--gold))',
    boxShadow: '0 2px 12px var(--gold-glow)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };

  const pagerBtnStyle = (disabled: boolean): React.CSSProperties => ({
    padding: '5px 14px',
    fontSize: 12,
    fontWeight: 700,
    borderRadius: 8,
    border: '1px solid var(--border-subtle)',
    background: 'transparent',
    color: disabled ? 'var(--text-muted)' : 'var(--text-primary)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  });

  const onDeleteUser = (idx: number) => {
    if (confirm(t('confirm', lang) + '?')) {
      deleteUser(idx);
      toast(t('deleteUser', lang), 'success');
    }
  };

  /* Icons for stat cards */
  const usersIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
  const shieldIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
  const briefcaseIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
    </svg>
  );
  const userCheckIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/>
      <polyline points="17 11 19 13 23 9"/>
    </svg>
  );

  /* Inline icon helpers */
  const goldUsersIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold, #F5A623)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
  const goldShieldIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold, #F5A623)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
  const plusIcon = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
  const pencilIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
  const trashIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--red, #DC2626)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
  );

  const tableThStyle: React.CSSProperties = {
    padding: '10px 16px',
    textAlign: 'left',
    fontWeight: 700,
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-muted)',
    borderBottom: '1px solid var(--border-subtle)',
  };

  return (
    <div style={{ padding: 0 }}>
      {/* Page title removed */}

      {/* Gradient stat cards */}
      <div className="stat-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
        <StatCard
          icon={usersIcon}
          label={t('totalUsers', lang)}
          value={stats.total}
          sub={t('totalUsers', lang)}
          gradient="linear-gradient(135deg, #3B7BF7, #2563EB)"
        />
        <StatCard
          icon={shieldIcon}
          label={t('admin', lang)}
          value={stats.admins}
          sub={t('admin', lang)}
          gradient="linear-gradient(135deg, #FF7086, #DC2626)"
        />
        <StatCard
          icon={briefcaseIcon}
          label={t('owner', lang)}
          value={stats.owners}
          sub={t('owner', lang)}
          gradient="linear-gradient(135deg, #FBBF24, #F97316)"
        />
        <StatCard
          icon={userCheckIcon}
          label={t('viewer', lang) || 'Viewer'}
          value={stats.viewers}
          sub={t('viewer', lang) || 'Viewer'}
          gradient="linear-gradient(135deg, #10B77F, #059669)"
        />
      </div>

      {/* User table */}
      <div style={panelStyle}>
        <div style={{ ...headStyle, flexWrap: 'wrap', gap: 12 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {goldUsersIcon}
            {t('allUsers', lang)}
            <span style={{
              background: 'linear-gradient(135deg, var(--amber), var(--gold))',
              color: '#0C1222',
              fontSize: 10,
              fontWeight: 800,
              padding: '2px 8px',
              borderRadius: 20,
              marginLeft: 4,
            }}>
              {/* While searching this is the match count, not the tenant total */}
              {isDbMode ? total : shownUsers.length}
            </span>
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span style={{ position: 'absolute', left: 10, display: 'inline-flex', pointerEvents: 'none', color: 'var(--text-muted)' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                className="form-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') setQuery(''); }}
                placeholder={lang === 'th' ? 'ค้นหาชื่อ / อีเมล / สิทธิ์…' : 'Search name / email / role…'}
                style={{ width: 260, maxWidth: '100%', paddingLeft: 30, paddingRight: query ? 28 : 12, fontSize: 12, fontWeight: 500 }}
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  title={lang === 'th' ? 'ล้างคำค้น' : 'Clear'}
                  style={{ position: 'absolute', right: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: 5, border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', padding: 0 }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              )}
            </div>
            {pkg?.limits && (() => {
              const used = isDbMode ? total : shownUsers.length;
              const full = used >= pkg.limits.users;
              return (
                <span
                  title={lang === 'th'
                    ? `แพ็กเกจ ${pkg.package} รองรับผู้ใช้ ${pkg.limits.users} คน`
                    : `${pkg.package} allows ${pkg.limits.users} users`}
                  style={{
                    fontSize: 11.5, fontWeight: 700, padding: '5px 11px', borderRadius: 99,
                    whiteSpace: 'nowrap',
                    color: full ? 'var(--amber)' : 'var(--text-muted)',
                    background: full ? 'color-mix(in srgb, var(--amber) 13%, transparent)' : 'transparent',
                    border: `1px solid ${full ? 'color-mix(in srgb, var(--amber) 30%, transparent)' : 'var(--glass-border)'}`,
                  }}
                >
                  {lang === 'th' ? 'ผู้ใช้' : 'Users'} {used}/{pkg.limits.users}
                  {full ? (lang === 'th' ? ' · เต็ม' : ' · full') : ''}
                </span>
              );
            })()}
            <button style={btnStyle} onClick={() => { setEditDbUser(null); setUserModalMode('add'); setShowUserModal(true); }}>
              {plusIcon}
              {t('addUser', lang)}
            </button>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <th style={tableThStyle}></th>
                <th style={tableThStyle}>Name</th>
                <th style={tableThStyle}>{t('email', lang)}</th>
                <th style={tableThStyle}>{t('role', lang)}</th>
                {isDbMode ? (
                  <>
                    <th style={tableThStyle}>Status</th>
                    <th style={{ ...tableThStyle, textAlign: 'right' }}>Last sign-in</th>
                  </>
                ) : (
                  <>
                    <th style={tableThStyle}>{lang === 'th' ? 'ไซต์' : 'Sites'}</th>
                    <th style={{ ...tableThStyle, textAlign: 'right' }}>Actions</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {isDbMode && loading && (
                <tr><td colSpan={6} style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</td></tr>
              )}
              {isDbMode && !loading && loadError && (
                <tr><td colSpan={6} style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--red)', fontSize: 13 }}>{loadError}</td></tr>
              )}
              {isDbMode && !loading && !loadError && dbUsers.length === 0 && (
                <tr><td colSpan={6} style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  {search
                    ? (lang === 'th' ? `ไม่พบผู้ใช้ที่ตรงกับ “${search}”` : `No users match “${search}”`)
                    : 'No users found'}
                </td></tr>
              )}
              {!isDbMode && shownUsers.length === 0 && (
                <tr><td colSpan={6} style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  {search
                    ? (lang === 'th' ? `ไม่พบผู้ใช้ที่ตรงกับ “${search}”` : `No users match “${search}”`)
                    : 'No users found'}
                </td></tr>
              )}
              {isDbMode && !loading && !loadError && dbUsers.map((u, idx) => {
                const role = userlevelToRole(u.userlevel);
                const rc = roleColors[role] || roleColors.viewer;
                const verified = (u.status || '').toLowerCase() === 'verified';
                return (
                  <tr
                    key={u.id}
                    style={{
                      borderBottom: '1px solid var(--border-subtle)',
                      background: hoveredRow === idx ? 'var(--bg-elevated)' : 'transparent',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={() => setHoveredRow(idx)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <td style={{ padding: '10px 16px' }}>
                      <Avatar name={u.name} size={30} />
                    </td>
                    <td style={{ padding: '10px 16px', fontWeight: 700, color: 'var(--text-primary)', fontSize: 13 }}>{u.name}</td>
                    <td style={{ padding: '10px 16px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.01em' }}>{u.email}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 6, background: rc.bg, color: rc.text, textTransform: 'capitalize' }}>
                        {role}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '3px 10px',
                        borderRadius: 6,
                        background: verified ? 'var(--green-dim)' : 'var(--amber-dim)',
                        color: verified ? 'var(--green)' : 'var(--amber)',
                        textTransform: 'capitalize',
                      }}>
                        {u.status || 'unknown'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', color: 'var(--text-secondary)', fontSize: 12 }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end' }}>
                        <span>{fmtDate(u.last_signin)}</span>
                        <button
                          title={t('edit', lang)}
                          onClick={() => { setEditDbUser({ id: u.id, name: u.name, email: u.email, role }); setUserModalMode('edit'); setShowUserModal(true); }}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 7, border: '1px solid var(--blue-dim)', background: 'transparent', color: 'var(--blue)', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: 'inherit' }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                          {t('edit', lang)}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!isDbMode && shownUsers.map(({ u: user, idx }) => {
                const rc = roleColors[user.role] || roleColors.viewer;
                const plantNames = user.plants
                  .map((pid) => plants[pid]?.name)
                  .filter(Boolean)
                  .join(', ');
                return (
                  <tr
                    key={idx}
                    style={{
                      borderBottom: '1px solid var(--border-subtle)',
                      background: hoveredRow === idx ? 'var(--bg-elevated)' : 'transparent',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={() => setHoveredRow(idx)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <td style={{ padding: '10px 16px' }}>
                      <Avatar name={user.name} size={30} />
                    </td>
                    <td style={{ padding: '10px 16px', fontWeight: 700, color: 'var(--text-primary)', fontSize: 13 }}>{user.name}</td>
                    <td style={{ padding: '10px 16px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.01em' }}>{user.email}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '3px 10px',
                          borderRadius: 6,
                          background: rc.bg,
                          color: rc.text,
                          textTransform: 'capitalize',
                        }}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px', color: 'var(--text-secondary)', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {plantNames || '\u2014'}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => { setUserModalMode('edit'); setEditUserIdx(idx); setShowUserModal(true); }}
                          className="btn btn-ghost"
                          style={{
                            padding: '5px 8px',
                            borderRadius: 6,
                            border: '1px solid var(--border-subtle)',
                            background: 'transparent',
                            color: 'var(--text-muted)',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title={t('edit', lang)}
                        >
                          {pencilIcon}
                        </button>
                        <button
                          onClick={() => onDeleteUser(idx)}
                          className="btn btn-ghost"
                          style={{
                            padding: '5px 8px',
                            borderRadius: 6,
                            border: '1px solid rgba(220,38,38,0.2)',
                            background: 'transparent',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title={t('delete', lang)}
                        >
                          {trashIcon}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {isDbMode && !loadError && total > PAGE_SIZE && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 20px',
            borderTop: '1px solid var(--border-subtle)',
            fontSize: 12,
            color: 'var(--text-muted)',
          }}>
            <span>
              {(page - 1) * PAGE_SIZE + 1}{'–'}{Math.min(page * PAGE_SIZE, total)} of {total}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                style={pagerBtnStyle(page <= 1 || loading)}
              >
                {t('prev', lang) || 'Prev'}
              </button>
              <span style={{ color: 'var(--text-secondary)' }}>{page} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                style={pagerBtnStyle(page >= totalPages || loading)}
              >
                {t('next', lang) || 'Next'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/*
        คำอธิบายบทบาท — เดิมเป็นตารางติ๊กถูก 9 แถว (Role Permissions Matrix)
        ที่เลิกตรงกับความจริงไปแล้ว

        ปัญหาของตารางนั้นคือมันอ่านเหมือนเป็นภาพรวมทั้งหมด ทั้งที่แสดงเฉพาะ
        สิทธิ์จาก PERMS (role) ชุดเดียว ส่วนสิทธิ์จริงวันนี้มาจากสามชุดที่
        ไม่ขึ้นต่อกัน:
          · PERMS ตาม role
          · dbUser.features ตามแพ็กเกจ (สอบเทียบ · PM · AI · รายงานอัตโนมัติ)
          · ACL ต่อไซต์/อุปกรณ์ (owner · admin · viewer ของไซต์นั้น)
        คนอ่านจึงเข้าใจว่า "เป็น Admin แล้วแก้ทุกไซต์ได้" ซึ่งไม่จริง

        และตารางยังบอกถึงหน้าที่ไม่มีแล้ว (Energy · Environment ไม่มีปุ่มใน
        sidebar) พร้อมกับไม่บอกถึงหน้าที่เพิ่มเข้ามา (ศูนย์แจ้งเตือน · สอบเทียบ ·
        ศูนย์รายงาน · จอรวมทุกไซต์ · AI ทุกไซต์)

        เลือกเขียนเป็นคำแทนตาราง เพราะตารางติ๊กถูกจะล้าสมัยทุกครั้งที่เพิ่มเมนู
        ส่วนคำอธิบายระดับนี้อยู่ได้นานกว่าและไม่ทำให้เข้าใจผิดว่าครบแล้ว
      */}
      <div style={panelStyle}>
        <div style={{ ...headStyle, justifyContent: 'flex-start', gap: 8 }}>
          {goldShieldIcon}
          {lang === 'th' ? 'บทบาทต่างกันอย่างไร' : 'What the roles mean'}
        </div>
        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {([
            {
              role: 'Admin', color: 'var(--red)', bg: 'var(--red-dim)',
              th: 'จัดการผู้ใช้ในองค์กรได้ เพิ่ม/แก้/ลบบัญชี และกำหนดบทบาทให้คนอื่น',
              en: 'Manages people — add, edit and remove accounts, and set other users\u2019 roles.',
            },
            {
              role: 'Owner', color: 'var(--accent)', bg: 'var(--gold-dim)',
              th: 'ตั้งค่าไซต์และอุปกรณ์ได้ แต่จัดการบัญชีผู้ใช้คนอื่นไม่ได้',
              en: 'Configures sites and devices, but cannot manage other people\u2019s accounts.',
            },
            {
              role: 'Viewer', color: 'var(--green)', bg: 'var(--green-dim)',
              th: 'ดูข้อมูลและรายงานได้อย่างเดียว แก้ไขอะไรไม่ได้',
              en: 'Reads data and reports only — cannot change anything.',
            },
          ]).map((r) => (
            <div key={r.role} style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{
                padding: '3px 11px', borderRadius: 6, fontSize: 11.5, fontWeight: 700,
                background: r.bg, color: r.color, flexShrink: 0, minWidth: 64, textAlign: 'center',
              }}>
                {r.role}
              </span>
              <span style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.65, flex: 1, minWidth: 200 }}>
                {lang === 'th' ? r.th : r.en}
              </span>
            </div>
          ))}

          {/* ข้อนี้สำคัญกว่าตารางทั้งตาราง — บทบาทไม่ได้ให้สิทธิ์ข้ามไซต์ */}
          <div style={{
            marginTop: 4, padding: '12px 14px', borderRadius: 10,
            background: 'var(--bg-elevated)', border: '1px solid var(--glass-border)',
            borderLeft: '3px solid var(--accent)',
            fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.7,
          }}>
            {lang === 'th'
              ? 'บทบาทกำหนดว่าเปิดเมนูไหนได้ แต่ไม่ได้ให้สิทธิ์ข้ามไซต์ — การแก้ไขไซต์หรืออุปกรณ์ยังต้องเป็นเจ้าของหรือแอดมินของไซต์นั้นด้วย และบางฟีเจอร์ (สอบเทียบ · บำรุงรักษา · AI · รายงานอัตโนมัติ) ขึ้นกับแพ็กเกจของบัญชี'
              : 'Roles decide which menus open, not access across sites — editing a site or device still requires being that site\u2019s owner or admin. Some features (calibration, maintenance, AI, automatic reports) also depend on the account\u2019s package.'}
          </div>
        </div>
      </div>
      <UserModal isOpen={showUserModal} onClose={() => setShowUserModal(false)} mode={userModalMode} userIndex={userModalMode === 'edit' ? editUserIdx : undefined} dbUser={userModalMode === 'edit' && editDbUser ? editDbUser : undefined} onSaved={() => setRefreshKey((k) => k + 1)} />
    </div>
  );
}
